import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trophy, X, Check, RotateCcw, Sparkles, Brain, ChevronRight, Home, Star } from 'lucide-react';

// Confetti function
const triggerConfetti = () => {
  try {
    import('canvas-confetti').then((confettiModule) => {
      const confetti = confettiModule.default;
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#06B6D4', '#3B82F6', '#8B5CF6']
      });
    });
  } catch (e) {
    console.log('Confetti not available');
  }
};

// Quiz Questions sur l'astrologie
const QUIZ_QUESTIONS = [
  {
    id: 1,
    category: "Les Big Three",
    question: "Que représente le signe solaire en astrologie ?",
    options: [
      "L'image que l'on projette aux autres",
      "L'identité profonde et la conscience de soi",
      "Les émotions et l'inconscient",
      "La communication et l'intellect"
    ],
    correctAnswer: 1,
    explanation: "Le signe solaire représente votre identité fondamentale, votre ego, votre volonté et la façon dont vous brillez dans le monde. C'est souvent ce qu'on appelle 'votre signe' en astrologie populaire."
  },
  {
    id: 2,
    category: "Les Big Three",
    question: "Que révèle le signe lunaire ?",
    options: [
      "Votre carrière idéale",
      "Votre compatibilité amoureuse",
      "Vos émotions, besoins intérieurs et réactions instinctives",
      "Votre apparence physique"
    ],
    correctAnswer: 2,
    explanation: "Le signe lunaire gouverne vos émotions profondes, votre inconscient, vos besoins de sécurité émotionnelle et la façon dont vous réagissez instinctivement aux situations."
  },
  {
    id: 3,
    category: "Les Big Three",
    question: "L'ascendant représente :",
    options: [
      "Le signe où se trouvait le Soleil à la naissance",
      "Le signe qui se levait à l'horizon Est au moment de la naissance",
      "Le signe opposé à votre signe solaire",
      "Le signe de votre parent dominant"
    ],
    correctAnswer: 1,
    explanation: "L'ascendant est le signe qui se levait à l'horizon Est au moment précis de votre naissance. Il représente votre masque social, votre apparence et la première impression que vous donnez."
  },
  {
    id: 4,
    category: "Les Planètes",
    question: "Quelle planète gouverne l'amour et les relations ?",
    options: [
      "Mars",
      "Mercure",
      "Vénus",
      "Jupiter"
    ],
    correctAnswer: 2,
    explanation: "Vénus est la planète de l'amour, de la beauté, de l'harmonie et des relations. Elle indique comment vous aimez et ce que vous recherchez dans l'amour."
  },
  {
    id: 5,
    category: "Les Planètes",
    question: "Mars en astrologie représente :",
    options: [
      "La communication et les voyages",
      "L'action, l'énergie et l'affirmation de soi",
      "L'expansion et la chance",
      "Les limites et la discipline"
    ],
    correctAnswer: 1,
    explanation: "Mars représente l'énergie, l'action, la volonté, le courage et la façon dont vous poursuivez vos désirs. C'est aussi la planète de la passion et parfois de l'agressivité."
  },
  {
    id: 6,
    category: "Les Planètes",
    question: "Saturne est associé à :",
    options: [
      "La chance et l'abondance",
      "Les émotions et l'intuition",
      "Les restrictions, la discipline et les leçons de vie",
      "La communication et la technologie"
    ],
    correctAnswer: 2,
    explanation: "Saturne est le grand maître de discipline du zodiaque. Il représente les limites, les responsabilités, la maturité et les leçons karmiques à apprendre."
  },
  {
    id: 7,
    category: "Les Planètes",
    question: "Quelle planète est associée à l'expansion et à la chance ?",
    options: [
      "Saturne",
      "Pluton",
      "Jupiter",
      "Neptune"
    ],
    correctAnswer: 2,
    explanation: "Jupiter est la planète de l'expansion, de l'optimisme, de la chance et de la croissance. Elle indique où vous trouvez naturellement l'abondance et les opportunités."
  },
  {
    id: 8,
    category: "Les Maisons",
    question: "Combien y a-t-il de maisons dans un thème natal ?",
    options: [
      "10 maisons",
      "12 maisons",
      "8 maisons",
      "14 maisons"
    ],
    correctAnswer: 1,
    explanation: "Le thème natal est divisé en 12 maisons, chacune représentant un domaine de vie différent (identité, finances, communication, foyer, créativité, santé, etc.)."
  },
  {
    id: 9,
    category: "Les Maisons",
    question: "La Maison 7 est traditionnellement associée à :",
    options: [
      "La carrière et la réputation",
      "Les partenariats et le mariage",
      "Les finances personnelles",
      "La santé et le travail quotidien"
    ],
    correctAnswer: 1,
    explanation: "La Maison 7 est la maison des partenariats, du mariage, des contrats et des relations engagées. Elle représente ce que vous recherchez chez l'autre."
  },
  {
    id: 10,
    category: "Les Maisons",
    question: "Quelle maison représente la carrière et la réputation publique ?",
    options: [
      "La Maison 2",
      "La Maison 6",
      "La Maison 10 (Milieu du Ciel)",
      "La Maison 12"
    ],
    correctAnswer: 2,
    explanation: "La Maison 10, aussi appelée Milieu du Ciel (MC), représente la carrière, les ambitions, la réputation publique et ce que vous voulez accomplir dans le monde."
  },
  {
    id: 11,
    category: "Les Aspects",
    question: "Un aspect de conjonction signifie que deux planètes sont :",
    options: [
      "Opposées dans le zodiaque",
      "Au même endroit ou très proches (0-10°)",
      "En angle de 90°",
      "En angle de 120°"
    ],
    correctAnswer: 1,
    explanation: "La conjonction (0°) est l'aspect le plus puissant. Les énergies des deux planètes fusionnent et s'intensifient mutuellement, créant un point focal majeur."
  },
  {
    id: 12,
    category: "Les Aspects",
    question: "Un trigone (120°) est généralement considéré comme :",
    options: [
      "Un aspect de tension et de défi",
      "Un aspect harmonieux et fluide",
      "Un aspect neutre",
      "Un aspect de blocage"
    ],
    correctAnswer: 1,
    explanation: "Le trigone (120°) est l'aspect le plus harmonieux. Il indique un flux naturel d'énergie entre les planètes, des talents innés et des facilités."
  },
  {
    id: 13,
    category: "Les Aspects",
    question: "Le carré (90°) représente :",
    options: [
      "Une harmonie parfaite",
      "Une tension créatrice et des défis à surmonter",
      "Une fusion d'énergies",
      "Une absence de connexion"
    ],
    correctAnswer: 1,
    explanation: "Le carré (90°) crée une tension dynamique qui pousse à l'action et à la croissance. C'est un aspect de défi qui, bien géré, devient source de force."
  },
  {
    id: 14,
    category: "Les Signes",
    question: "Les signes de Feu sont :",
    options: [
      "Taureau, Vierge, Capricorne",
      "Gémeaux, Balance, Verseau",
      "Bélier, Lion, Sagittaire",
      "Cancer, Scorpion, Poissons"
    ],
    correctAnswer: 2,
    explanation: "Les signes de Feu (Bélier, Lion, Sagittaire) sont caractérisés par l'énergie, l'enthousiasme, la passion et l'action spontanée."
  },
  {
    id: 15,
    category: "Les Signes",
    question: "Quel élément caractérise Cancer, Scorpion et Poissons ?",
    options: [
      "Terre",
      "Air",
      "Feu",
      "Eau"
    ],
    correctAnswer: 3,
    explanation: "Cancer, Scorpion et Poissons sont les signes d'Eau. Ils sont gouvernés par les émotions, l'intuition, la sensibilité et la profondeur psychologique."
  },
  {
    id: 16,
    category: "Les Transits",
    question: "Qu'est-ce qu'un transit en astrologie ?",
    options: [
      "La position d'une planète à la naissance",
      "Le mouvement actuel des planètes par rapport au thème natal",
      "Un aspect négatif entre deux planètes",
      "Le changement de signe d'une planète natale"
    ],
    correctAnswer: 1,
    explanation: "Les transits sont les positions actuelles des planètes dans le ciel et leurs aspects avec votre thème natal. Ils indiquent les influences et thèmes du moment présent."
  },
  {
    id: 17,
    category: "Les Transits",
    question: "Le retour de Saturne se produit environ tous les :",
    options: [
      "12 ans",
      "18 ans",
      "29 ans",
      "7 ans"
    ],
    correctAnswer: 2,
    explanation: "Saturne met environ 29 ans à parcourir le zodiaque. Le retour de Saturne (vers 29-30 ans et 58-60 ans) marque des périodes majeures de maturité et de bilan de vie."
  },
  {
    id: 18,
    category: "Rétrogradation",
    question: "Mercure rétrograde est souvent associé à :",
    options: [
      "La chance en amour",
      "Des problèmes de communication et de technologie",
      "Une période d'expansion financière",
      "Une énergie physique accrue"
    ],
    correctAnswer: 1,
    explanation: "Mercure rétrograde est traditionnellement lié à des malentendus, des retards, des problèmes technologiques et de communication. C'est une période propice à la révision plutôt qu'à l'initiation."
  },
  {
    id: 19,
    category: "Histoire",
    question: "L'astrologie occidentale moderne est basée sur :",
    options: [
      "Le zodiaque sidéral (constellations actuelles)",
      "Le zodiaque tropical (saisons)",
      "La position des étoiles fixes",
      "Les phases lunaires uniquement"
    ],
    correctAnswer: 1,
    explanation: "L'astrologie occidentale utilise le zodiaque tropical, basé sur les saisons et l'équinoxe de printemps (0° Bélier), plutôt que sur la position actuelle des constellations."
  },
  {
    id: 20,
    category: "Principes",
    question: "Le libre arbitre en astrologie signifie que :",
    options: [
      "L'astrologie prédit exactement l'avenir",
      "Les astres inclinent mais ne déterminent pas",
      "Tout est écrit dans les étoiles",
      "Seul le signe solaire compte"
    ],
    correctAnswer: 1,
    explanation: "L'astrologie moderne considère que les astres révèlent des tendances et potentiels, mais le libre arbitre permet de choisir comment les vivre. 'Les astres inclinent, ils n'obligent pas.'"
  }
];

const AstrozodiacQuizPage = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const question = QUIZ_QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / QUIZ_QUESTIONS.length) * 100;

  const handleAnswerSelect = (index) => {
    if (showExplanation) return;
    setSelectedAnswer(index);
  };

  const handleValidate = () => {
    if (selectedAnswer === null) return;

    const isCorrect = selectedAnswer === question.correctAnswer;
    
    setAnswers([...answers, {
      questionId: question.id,
      selected: selectedAnswer,
      correct: question.correctAnswer,
      isCorrect
    }]);

    if (isCorrect) {
      setScore(score + 1);
    }

    setShowExplanation(true);
  };

  const handleNext = () => {
    if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setQuizCompleted(true);
      if (score >= QUIZ_QUESTIONS.length * 0.7) {
        triggerConfetti();
      }
    }
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setScore(0);
    setAnswers([]);
    setQuizCompleted(false);
    setShowExplanation(false);
  };

  const getScoreMessage = () => {
    const percentage = (score / QUIZ_QUESTIONS.length) * 100;
    if (percentage >= 90) return { title: "Excellent !", message: "Tu es un véritable astrologue !", emoji: "🏆" };
    if (percentage >= 70) return { title: "Très bien !", message: "Tu maîtrises les bases de l'astrologie !", emoji: "🌟" };
    if (percentage >= 50) return { title: "Bien !", message: "Continue à explorer les étoiles.", emoji: "👍" };
    return { title: "Courage !", message: "Découvre la base de connaissances pour progresser.", emoji: "📚" };
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Les Big Three": "bg-gradient-to-r from-amber-500 to-orange-500",
      "Les Planètes": "bg-gradient-to-r from-purple-500 to-indigo-500",
      "Les Maisons": "bg-gradient-to-r from-cyan-500 to-teal-500",
      "Les Aspects": "bg-gradient-to-r from-rose-500 to-pink-500",
      "Les Signes": "bg-gradient-to-r from-blue-500 to-cyan-500",
      "Les Transits": "bg-gradient-to-r from-amber-400 to-yellow-500",
      "Rétrogradation": "bg-gradient-to-r from-violet-500 to-purple-600",
      "Histoire": "bg-gradient-to-r from-slate-500 to-slate-600",
      "Principes": "bg-gradient-to-r from-cyan-600 to-blue-600"
    };
    return colors[category] || "bg-slate-600";
  };

  if (quizCompleted) {
    const scoreInfo = getScoreMessage();
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50 py-12 px-4 md:px-8">
        {/* Starfield */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <div key={i} className="absolute w-1 h-1 bg-cyan-300 rounded-full animate-pulse"
              style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.4 + 0.1, animationDelay: `${Math.random() * 3}s` }} />
          ))}
        </div>
        
        <div className="max-w-2xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/60 rounded-3xl border border-sky-200 shadow-lg p-8 text-center backdrop-blur-sm"
          >
            <div className="text-6xl mb-6">{scoreInfo.emoji}</div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">{scoreInfo.title}</h1>
            <p className="text-sky-500 text-lg mb-8">{scoreInfo.message}</p>
            
            <div className="bg-gray-50 rounded-2xl p-6 mb-8 border border-sky-100">
              <div className="flex items-center justify-center gap-4 mb-4">
                <Trophy className="h-8 w-8 text-amber-400" />
                <span className="text-4xl font-bold text-amber-400">{score}/{QUIZ_QUESTIONS.length}</span>
              </div>
              <p className="text-sm text-sky-500">
                {Math.round((score / QUIZ_QUESTIONS.length) * 100)}% de bonnes réponses
              </p>
            </div>

            {/* Detailed Results */}
            <div className="text-left mb-8">
              <h3 className="font-semibold mb-4 text-gray-900">Détail par catégorie :</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {Object.entries(
                  answers.reduce((acc, answer) => {
                    const q = QUIZ_QUESTIONS.find(q => q.id === answer.questionId);
                    if (!acc[q.category]) acc[q.category] = { correct: 0, total: 0 };
                    acc[q.category].total++;
                    if (answer.isCorrect) acc[q.category].correct++;
                    return acc;
                  }, {})
                ).map(([category, stats]) => (
                  <div key={category} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-700">
                      <span className={`w-3 h-3 rounded-full ${getCategoryColor(category).replace('bg-gradient-to-r ', 'bg-')}`}></span>
                      {category}
                    </span>
                    <span className={stats.correct === stats.total ? "text-sky-600 font-medium" : "text-gray-600"}>
                      {stats.correct}/{stats.total}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={restartQuiz}
                className="flex-1 rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-cyan-400 hover:to-blue-400"
                data-testid="restart-quiz-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Recommencer
              </Button>
              <Button
                onClick={() => navigate('/astrozodiac/knowledge')}
                variant="outline"
                className="flex-1 rounded-full border-sky-300 text-sky-500 hover:bg-sky-50"
              >
                <Brain className="h-4 w-4 mr-2" />
                Apprendre
              </Button>
              <Button
                onClick={() => navigate('/astrozodiac')}
                variant="ghost"
                className="flex-1 rounded-full text-gray-700 hover:bg-white"
              >
                <Home className="h-4 w-4 mr-2" />
                Accueil
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50 py-12 px-4 md:px-8">
      {/* Starfield */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div key={i} className="absolute w-1 h-1 bg-cyan-300 rounded-full animate-pulse"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.4 + 0.1, animationDelay: `${Math.random() * 3}s` }} />
        ))}
      </div>
      
      <div className="max-w-3xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/astrozodiac')}
            className="mb-6 rounded-full text-sky-500 hover:bg-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
                <Star className="h-5 w-5 text-gray-900" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Quiz Astrologie</h1>
                <p className="text-sm text-sky-600">Teste tes connaissances célestes</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-sky-600">{currentQuestion + 1}/{QUIZ_QUESTIONS.length}</p>
              <p className="text-xs text-gray-600">Score: {score}</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="h-2 bg-white rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-sky-500 to-cyan-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </motion.div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="bg-white/60 rounded-3xl border border-sky-200 shadow-lg overflow-hidden backdrop-blur-sm"
          >
            {/* Category Badge */}
            <div className={`px-6 py-3 ${getCategoryColor(question.category)}`}>
              <span className="text-gray-900 text-sm font-medium">{question.category}</span>
            </div>

            <div className="p-6 md:p-8">
              {/* Question */}
              <h2 className="text-xl md:text-2xl mb-8 leading-relaxed text-gray-900 font-semibold">
                {question.question}
              </h2>

              {/* Options */}
              <div className="space-y-3 mb-8">
                {question.options.map((option, index) => {
                  const isSelected = selectedAnswer === index;
                  const isCorrect = index === question.correctAnswer;
                  const showCorrectness = showExplanation;

                  let bgClass = "bg-gray-50 hover:bg-sky-50 border-gray-200";
                  let iconClass = null;

                  if (showCorrectness) {
                    if (isCorrect) {
                      bgClass = "bg-sky-50 border-sky-500";
                      iconClass = <Check className="h-5 w-5 text-sky-600" />;
                    } else if (isSelected && !isCorrect) {
                      bgClass = "bg-red-50 border-red-400";
                      iconClass = <X className="h-5 w-5 text-red-500" />;
                    }
                  } else if (isSelected) {
                    bgClass = "bg-sky-100 border-sky-400";
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(index)}
                      disabled={showExplanation}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${bgClass} ${
                        showExplanation ? 'cursor-default' : 'cursor-pointer'
                      }`}
                      data-testid={`option-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          isSelected && !showCorrectness ? 'bg-sky-500 text-white' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span className="text-sm md:text-base text-gray-700">{option}</span>
                      </div>
                      {iconClass}
                    </button>
                  );
                })}
              </div>

              {/* Explanation */}
              <AnimatePresence>
                {showExplanation && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6"
                  >
                    <div className={`p-4 rounded-xl ${
                      selectedAnswer === question.correctAnswer 
                        ? 'bg-sky-50 border border-sky-300' 
                        : 'bg-amber-50 border border-amber-300'
                    }`}>
                      <div className="flex items-start gap-3">
                        <Sparkles className={`h-5 w-5 mt-0.5 ${
                          selectedAnswer === question.correctAnswer 
                            ? 'text-sky-600' 
                            : 'text-amber-600'
                        }`} />
                        <div>
                          <p className={`font-medium mb-1 ${
                            selectedAnswer === question.correctAnswer 
                              ? 'text-sky-700' 
                              : 'text-amber-700'
                          }`}>
                            {selectedAnswer === question.correctAnswer ? 'Correct !' : 'Explication'}
                          </p>
                          <p className={`text-sm ${
                            selectedAnswer === question.correctAnswer 
                              ? 'text-sky-600' 
                              : 'text-amber-600'
                          }`}>
                            {question.explanation}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="flex justify-end">
                {!showExplanation ? (
                  <Button
                    onClick={handleValidate}
                    disabled={selectedAnswer === null}
                    className="rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-cyan-400 hover:to-blue-400 px-8"
                    data-testid="validate-btn"
                  >
                    Valider
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    className="rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-cyan-400 hover:to-blue-400 px-8"
                    data-testid="next-btn"
                  >
                    {currentQuestion < QUIZ_QUESTIONS.length - 1 ? (
                      <>
                        Question suivante
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </>
                    ) : (
                      <>
                        Voir mes résultats
                        <Trophy className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AstrozodiacQuizPage;
